import { useEffect, useState } from "react";
import { MapPin, Clock, Calendar as CalIcon, FileText, MessageCircle, X } from "lucide-react";
import PageHero from "../components/PageHero";
import { fetchEvents } from "../lib/api";
import { SCHOOL } from "../lib/site";

export default function Eventi() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("Tutti");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetchEvents().then(setEvents).catch(() => {});
  }, []);

  const cats = ["Tutti", ...Array.from(new Set(events.map((e) => e.categoria)))];
  const filtered = filter === "Tutti" ? events : events.filter((e) => e.categoria === filter);

  // Group by anno
  const grouped = filtered.reduce((acc, ev) => {
    const y = ev.anno || new Date(ev.data).getFullYear() || "Da definire";
    (acc[y] = acc[y] || []).push(ev);
    return acc;
  }, {});
  const years = Object.keys(grouped).sort((a, b) => b - a);

  const waLink = `https://api.whatsapp.com/send?phone=${SCHOOL.whatsapp.number.replace("+", "")}&text=${encodeURIComponent("Salve, vorrei informazioni sui concerti e le rassegne")}`;

  return (
    <div data-testid="page-eventi">
      <PageHero
        eyebrow="Concerti · Rassegne · Festival"
        title="Attività di promozione"
        italic="culturale-musicale."
        subtitle="Concerti, rassegne, festival e saggi organizzati dall'Associazione sul territorio delle province di Ferrara e Rovigo. Eventi aperti al pubblico, in collaborazione con istituzioni e enti locali."
      />

      <section className="py-16 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        <div className="flex flex-wrap gap-2 mb-12" data-testid="event-filters">
          {cats.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              data-testid={`event-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-[11px] tracking-eyebrow px-4 py-2 rounded-sm border transition ${
                filter === cat
                  ? "bg-[#A3133A] text-white border-[#A3133A]"
                  : "bg-transparent text-[#A3133A] border-[#A3133A]/30 hover:border-[#A3133A]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {years.map((year) => (
          <div key={year} className="mb-20" data-testid={`year-section-${year}`}>
            <div className="flex items-end gap-6 mb-10 border-b border-[#A3133A]/15 pb-4">
              <div className="font-serif-display text-6xl sm:text-7xl text-[#A3133A] leading-none">{year}</div>
              <div className="text-[11px] tracking-eyebrow text-[#5C5C5C] pb-2">
                {grouped[year].length} eventi
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {grouped[year].map((ev) => (
                <article
                  key={ev.id}
                  data-testid={`event-${ev.id}`}
                  className="group flex flex-col bg-[#FBFBF9] border border-[#A3133A]/15 hover:border-[#A3133A]/40 transition"
                >
                  {ev.poster && (
                    <button
                      onClick={() => setSelected(ev)}
                      data-testid={`event-poster-${ev.id}`}
                      className="block w-full aspect-[3/4] overflow-hidden bg-[#A3133A]/5 relative"
                    >
                      <img
                        src={ev.poster}
                        alt={ev.titolo}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-[#A3133A]/0 group-hover:bg-[#A3133A]/30 transition" />
                    </button>
                  )}
                  <div className="p-7 flex-1 flex flex-col">
                    <div className="text-[10px] tracking-eyebrow text-[#A3133A] mb-3">{ev.categoria}</div>
                    <h3 className="font-serif-display text-2xl text-[#1C1C1C] mb-4 leading-tight">{ev.titolo}</h3>

                    <div className="space-y-2 text-[12px] text-[#5C5C5C] mb-4">
                      <div className="flex items-start gap-2">
                        <CalIcon size={12} className="mt-1 text-[#A3133A] shrink-0" />
                        <span>{ev.data}</span>
                      </div>
                      {ev.ora && ev.ora !== ev.data && (
                        <div className="flex items-start gap-2">
                          <Clock size={12} className="mt-1 text-[#A3133A] shrink-0" />
                          <span>{ev.ora}</span>
                        </div>
                      )}
                      <div className="flex items-start gap-2">
                        <MapPin size={12} className="mt-1 text-[#A3133A] shrink-0" />
                        <span>{ev.luogo}</span>
                      </div>
                    </div>

                    <p className="text-sm text-[#5C5C5C] leading-relaxed flex-1 whitespace-pre-line">{ev.descrizione}</p>

                    {ev.libretto_url && (
                      <a
                        href={ev.libretto_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`event-libretto-${ev.id}`}
                        className="mt-5 text-[12px] tracking-eyebrow text-[#A3133A] inline-flex items-center gap-2 link-underline self-start"
                      >
                        <FileText size={12} /> Scarica il libretto programma
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        ))}

        {/* CTA */}
        <div className="bg-[#A3133A] text-[#FBFBF9] p-10 md:p-16 relative overflow-hidden grain rounded-sm text-center">
          <div className="relative z-10">
            <div className="text-[11px] tracking-eyebrow opacity-80 mb-3">Informazioni e biglietti</div>
            <h3 className="font-serif-display text-4xl sm:text-5xl mb-5 leading-tight">
              Per dettagli, programma <em className="italic font-light">e prenotazioni</em>.
            </h3>
            <p className="opacity-90 max-w-2xl mx-auto mb-8 leading-relaxed">
              Contattaci per conoscere date precise, programmi musicali, location e modalità di accesso
              ai concerti e alle rassegne.
            </p>
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              data-testid="eventi-cta-whatsapp"
              className="bg-[#25D366] text-white px-8 py-4 text-[12px] tracking-eyebrow rounded-sm hover:bg-[#1FAE54] transition inline-flex items-center gap-2"
            >
              <MessageCircle size={14} /> Scrivi su WhatsApp
            </a>
            <div className="mt-6 text-sm opacity-90">
              Oppure: {SCHOOL.phones.map((p) => p.value).join(" · ")}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox poster */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="event-lightbox"
        >
          <button
            onClick={() => setSelected(null)}
            className="absolute top-6 right-6 text-white p-2"
            aria-label="Chiudi"
          >
            <X size={24} />
          </button>
          <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={selected.poster} alt={selected.titolo} className="w-full h-auto max-h-[85vh] object-contain" />
            <div className="text-center text-white/90 font-serif-display text-2xl mt-4">{selected.titolo}</div>
          </div>
        </div>
      )}
    </div>
  );
}
